/**
 * Property-type icons.
 *
 * Fourteen property types share one column in every table, and "Block of flats" / "Bungalow" /
 * "Semi-detached" are four words apart at a glance. A mark makes the row scannable — an agent
 * looking for the shop among twenty flats finds it by shape, not by reading.
 *
 * Drawn here rather than taken from an icon package for the same reason as `icons.tsx`: no set
 * ships fourteen building types of this kind, and the ones that come close are drawn on a
 * different grid, which is exactly what makes a mixed icon set look wrong.
 *
 * Same grid as `icons.tsx` — 24 viewBox, 1.75 stroke, rounded caps, `currentColor` — so these sit
 * beside the navigation icons without looking borrowed.
 *
 * Each one carries a single distinguishing feature, chosen so no two silhouettes collide at 16px:
 *
 * | Type            | What tells it apart                                   |
 * |-----------------|-------------------------------------------------------|
 * | Block of flats  | tall body with a stepped lower wing, window grid       |
 * | Duplex          | one house, two floors, a band across the middle        |
 * | Terrace         | a zig-zag roofline over a run of shared walls          |
 * | Semi-detached   | one roof split down the centre by a party wall         |
 * | Detached house  | a house standing alone, with a chimney                 |
 * | Bungalow        | wide and low, deep eaves, no upper floor               |
 * | Mini-flat       | the same plan with a partition — two rooms, one door    |
 * | Self-contained  | a plan view of one room, showing the door swing        |
 * | Shop / retail   | a scalloped awning                                     |
 * | Office          | flat roof, even window grid, no pitch anywhere         |
 * | Warehouse       | a full-width roller shutter with slats                 |
 * | Event centre    | a dome on columns                                      |
 * | Land            | a bare parcel with a survey flag                       |
 * | Mixed use       | flats above, a shop opening below an awning band       |
 */

import type { JSX, SVGProps } from "react";
import { PROPERTY_TYPES, type PropertyType, propertyTypeLabel } from "@rentorng/core";

type MarkProps = SVGProps<SVGSVGElement> & { size?: number };

function Glyph({ size = 18, children, ...rest }: MarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

const BlockOfFlats = (props: MarkProps) => (
  <Glyph {...props}>
    <path d="M4 21V5.2A1.5 1.5 0 0 1 5.5 3.7h8A1.5 1.5 0 0 1 15 5.2V21" />
    <path d="M15 10.5h3.5A1.5 1.5 0 0 1 20 12v9" />
    <path d="M7 7h1.8M10.8 7h1.8M7 11h1.8M10.8 11h1.8M7 15h1.8M10.8 15h1.8" />
    <path d="M2.8 21h18.4" />
  </Glyph>
);

const Duplex = (props: MarkProps) => (
  <Glyph {...props}>
    <path d="M3.2 10.6 12 4l8.8 6.6" />
    <path d="M5.4 9.6V21h13.2V9.6" />
    <path d="M5.4 15.2h13.2" />
    <path d="M8.4 11.6h2.2M13.4 11.6h2.2" />
    <path d="M10.2 21v-3.8h3.6V21" />
  </Glyph>
);

const Terrace = (props: MarkProps) => (
  <Glyph {...props}>
    <path d="M3 12.4 6 9.2l3 3.2 3-3.2 3 3.2 3-3.2 3 3.2" />
    <path d="M4.6 11.8V21h14.8v-9.2" />
    <path d="M9 21v-8.6M15 21v-8.6" />
    <path d="M2.8 21h18.4" />
  </Glyph>
);

const SemiDetached = (props: MarkProps) => (
  <Glyph {...props}>
    <path d="M2.6 11.6 12 4.4l9.4 7.2" />
    <path d="M4.8 10.6V21h14.4V10.6" />
    <path d="M12 21V10.2" />
    <path d="M7.2 21v-3.8h2.6V21M14.2 21v-3.8h2.6V21" />
  </Glyph>
);

const Detached = (props: MarkProps) => (
  <Glyph {...props}>
    <path d="M3.4 11.4 12 4.6l8.6 6.8" />
    <path d="M5.6 10.4V21h12.8V10.4" />
    <path d="M16.4 7.6V4.6h2.2v4.7" />
    <path d="M10.3 21v-5.2h3.4V21" />
    <path d="M7.6 13h2.2" />
  </Glyph>
);

const Bungalow = (props: MarkProps) => (
  <Glyph {...props}>
    <path d="M2 13 12 6.4 22 13" />
    <path d="M4.8 12.2V21h14.4v-8.8" />
    <path d="M10.3 21v-4.6h3.4V21" />
    <path d="M6.8 15.4h2.2M15 15.4h2.2" />
  </Glyph>
);

/**
 * A mini-flat, drawn as the plan it is.
 *
 * It is the self-contained plan with a wall through it: a parlour and a room behind one front
 * door. Drawing it that way is what keeps the two apart at 16px — as elevations they would be the
 * same low box, and the difference between them is inside, not outside. The partition carries the
 * gap where the internal doorway is, so it reads as two rooms of one home rather than as two homes.
 */
const MiniFlat = (props: MarkProps) => (
  <Glyph {...props}>
    <rect x="3.4" y="3.8" width="17.2" height="16.8" rx="2" />
    <path d="M13.6 3.8v5M13.6 12.4v8.2" />
    <path d="M6.6 20.6v-4.4" />
    <path d="M6.6 16.2a4.4 4.4 0 0 1 4.4 4.4" />
  </Glyph>
);

const SelfContained = (props: MarkProps) => (
  <Glyph {...props}>
    <rect x="3.4" y="3.8" width="17.2" height="16.8" rx="2" />
    <path d="M8.8 20.6v-6.2" />
    <path d="M8.8 14.4a6.2 6.2 0 0 1 6.2 6.2" />
    <path d="M6.6 8.2h5.2" />
  </Glyph>
);

const Shop = (props: MarkProps) => (
  <Glyph {...props}>
    <path d="M3.6 9.4 5.4 4.6h13.2l1.8 4.8" />
    <path d="M3.6 9.4c1.4 0 1.4 2.1 2.8 2.1s1.4-2.1 2.8-2.1 1.4 2.1 2.8 2.1 1.4-2.1 2.8-2.1 1.4 2.1 2.8 2.1 1.4-2.1 2.8-2.1" />
    <path d="M5 11.4V21h14v-9.6" />
    <path d="M9.4 21v-5.4h5.2V21" />
  </Glyph>
);

const Office = (props: MarkProps) => (
  <Glyph {...props}>
    <path d="M5 21V4.6a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1V21" />
    <path d="M8 7.2h2.2M13.8 7.2h2.2M8 10.8h2.2M13.8 10.8h2.2M8 14.4h2.2M13.8 14.4h2.2" />
    <path d="M10 21v-3.2h4V21" />
    <path d="M2.8 21h18.4" />
  </Glyph>
);

const Warehouse = (props: MarkProps) => (
  <Glyph {...props}>
    <path d="M2.4 11.2 12 6l9.6 5.2" />
    <path d="M4.6 10.4V21h14.8V10.4" />
    <path d="M7.8 21v-7.4h8.4V21" />
    <path d="M7.8 16.2h8.4M7.8 18.6h8.4" />
  </Glyph>
);

const EventCentre = (props: MarkProps) => (
  <Glyph {...props}>
    <path d="M12 3.2v1.6" />
    <path d="M4.4 11.4a7.6 7.6 0 0 1 15.2 0" />
    <path d="M2.8 11.4h18.4" />
    <path d="M5.6 11.6V21M9.9 11.6V21M14.1 11.6V21M18.4 11.6V21" />
    <path d="M2.8 21h18.4" />
  </Glyph>
);

const Land = (props: MarkProps) => (
  <Glyph {...props}>
    <path d="M12 9.6 21 14.6l-9 5-9-5 9-5Z" />
    <path d="M12 9.6V3.6" />
    <path d="M12 3.8h4.4l-1.3 1.7 1.3 1.7H12" />
  </Glyph>
);

const MixedUse = (props: MarkProps) => (
  <Glyph {...props}>
    <path d="M5 21V5.2a1.5 1.5 0 0 1 1.5-1.5h11A1.5 1.5 0 0 1 19 5.2V21" />
    <path d="M8 7.2h2.2M13.8 7.2h2.2M8 11h2.2M13.8 11h2.2" />
    <path d="M3.4 14.6h17.2" />
    <path d="M9.2 21v-4.6h5.6V21" />
    <path d="M2.8 21h18.4" />
  </Glyph>
);

export const PROPERTY_TYPE_ICONS: Record<PropertyType, (props: MarkProps) => JSX.Element> = {
  block_of_flats: BlockOfFlats,
  duplex: Duplex,
  terrace: Terrace,
  semi_detached: SemiDetached,
  detached: Detached,
  bungalow: Bungalow,
  mini_flat: MiniFlat,
  self_contained: SelfContained,
  shop: Shop,
  office: Office,
  warehouse: Warehouse,
  event_centre: EventCentre,
  land: Land,
  mixed_use: MixedUse,
};

const RESIDENTIAL = new Set<string>(PROPERTY_TYPES.filter((t) => t.residential).map((t) => t.value));

/** Whether a type houses people. Drives the tint, and nothing else. */
export function isResidentialType(type: string): boolean {
  return RESIDENTIAL.has(type);
}

/** An unknown type still renders something rather than leaving a hole in the row. */
function glyphFor(type: string) {
  return PROPERTY_TYPE_ICONS[type as PropertyType] ?? Office;
}

export function PropertyTypeIcon({ type, ...rest }: MarkProps & { type: string }) {
  const Component = glyphFor(type);
  return <Component {...rest} />;
}

/**
 * The icon in a tinted tile.
 *
 * Residential is brand green, everything commercial is blue — including land, which is bought and
 * let as an investment rather than lived in. Two families of colour plus fourteen shapes means the
 * type reads from across the desk, and the tint never carries meaning the shape does not also
 * carry, so it survives being printed in mono.
 */
export function PropertyTypeMark({
  type,
  size = 18,
  className = "",
}: {
  type: string;
  size?: number;
  className?: string;
}) {
  const box = size + 16;
  return (
    <span
      title={propertyTypeLabel(type as PropertyType)}
      style={{ width: box, height: box }}
      className={`inline-flex shrink-0 items-center justify-center rounded-[var(--radius-control)] ${
        isResidentialType(type) ? "bg-brand-50 text-brand-700" : "bg-info-50 text-info-700"
      } ${className}`}
    >
      <PropertyTypeIcon type={type} size={size} />
    </span>
  );
}

/** Icon plus label, for a cell where the words still need to be there. */
export function PropertyTypeTag({ type, className = "" }: { type: string; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <PropertyTypeIcon
        type={type}
        size={16}
        className={isResidentialType(type) ? "text-brand-600" : "text-info-600"}
      />
      <span>{propertyTypeLabel(type as PropertyType)}</span>
    </span>
  );
}

/**
 * The marks for a property that is more than one thing.
 *
 * Shows the primary large and the rest small beside it, up to a limit. A row has space for one
 * strong shape; the extras are a hint that there is more, not a second thing to read.
 */
export function PropertyTypeMarks({
  types,
  size = 17,
  max = 3,
  className = "",
}: {
  types: readonly string[] | null | undefined;
  size?: number;
  max?: number;
  className?: string;
}) {
  const list = (types ?? []).filter(Boolean).slice(0, max);
  if (!list.length) return null;
  const [primary, ...rest] = list;

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <PropertyTypeMark type={primary} size={size} />
      {rest.map((type) => (
        <PropertyTypeIcon
          key={type}
          type={type}
          size={Math.round(size * 0.8)}
          className={isResidentialType(type) ? "text-brand-500" : "text-info-500"}
        />
      ))}
      {(types ?? []).length > max ? (
        <span className="text-[11px] font-medium text-ink-400">+{(types ?? []).length - max}</span>
      ) : null}
    </span>
  );
}
